import { Box, ListItem, UnorderedList } from "@chakra-ui/react";
import { FragmentType, getFragmentData, gql } from "__generated__/gql";
import Address from "components/Shared/Address";
import PhoneNumberLink from "components/Shared/PhoneNumberLink";
import { formatInteger } from "utils/formatting";
import {
  NameWithFlagIcons,
  pricingStrategyMap,
  retailerCategoryReviewMap,
  slottingOptionsMap,
} from "components/Shared/RetailerDirectory/utils";
import TagList from "components/Shared/TagList";
import LabeledValueTable, {
  LabeledValueRow,
  LabeledValueTableProps,
} from "components/Shared/LabeledValueTable";
import ExternalDomainLink from "components/Shared/ExternalDomainLink";
import MarkdownText from "components/Shared/MarkdownText";
import MaxHeightExpander from "components/Shared/MaxHeightExpander";
import RetailerTypeBadge from "components/Shared/RetailerDirectory/RetailerTypeBadge";
import Link from "components/Shared/Link";
import BrokerTagList, {
  BrokerTagListItemFragment,
} from "components/Shared/BrokerTagList";
import {
  adminDistributionCenterPage,
  adminOrderingPlatformPage,
  customerPage,
  customerWarehousePage,
} from "utils/links";
import { RetailerType } from "__generated__/gql/graphql";
import MaybeRetailerLink from "./MaybeRetailerLink";

const fragment = gql(/* GraphQL */ `
  fragment RetailerDetails on Retailer {
    id
    numStores
    retailerType
    slottingRequired
    pricingStrategy
    categoryReview
    website
    description
    isCustom
    isArchived
    tags
    affiliation
    notes
    parent {
      id
      ...MaybeRetailerLinkFragment
    }
    childRetailersList(orderBy: NAME_ASC) {
      id
      ...MaybeRetailerLinkFragment
    }
    channelsList {
      id
      name
    }
    regionsList {
      id
      name
    }
    headquartersAddress {
      id
      ...AddressComponentFields
    }
    headquartersPhoneNumber {
      id
      ...PhoneNumberLinkFields
    }
    distributionCentersList(
      filter: { isArchived: { equalTo: false } }
      orderBy: FULL_DISPLAY_NAME_ASC
    ) {
      id
      name
      fullDisplayName
      orderingPlatformId
    }
    ...RetailerNameWithFlagIconsFragment
  }
`);

type Props = Omit<LabeledValueTableProps, "isLoaded"> & {
  brokers?: BrokerTagListItemFragment[] | null | undefined;
  data: FragmentType<typeof fragment> | null | undefined;
  isAdmin?: boolean;
  isLoading?: boolean;
  isNameIncluded?: boolean;
  onSelectedRetailerIdChange?: (retailerId: UUID) => void;
};

export default function RetailerDetails({
  children,
  data,
  brokers,
  isAdmin,
  isLoading,
  isNameIncluded,
  onSelectedRetailerIdChange,
  ...rest
}: Props) {
  const retailer = getFragmentData(fragment, data);

  return (
    <LabeledValueTable
      sx={{ tableLayout: "fixed" }}
      cellProps={{ py: 2 }}
      labelProps={{ width: "13em", textAlign: "right" }}
      isLoading={isLoading}
      {...rest}
    >
      {isNameIncluded && (
        <LabeledValueRow
          label="Name"
          value={<NameWithFlagIcons retailer={retailer} />}
        />
      )}

      <LabeledValueRow
        label="Type"
        value={
          retailer?.retailerType && (
            <RetailerTypeBadge
              retailerType={retailer.retailerType}
              alignSelf="flex-start"
            />
          )
        }
      />

      <LabeledValueRow
        label="Website"
        value={
          retailer?.website && (
            <ExternalDomainLink href={retailer.website} isTruncated hasIcon />
          )
        }
      />

      <LabeledValueRow
        label="Store Count"
        value={formatInteger(retailer?.numStores)}
      />

      <LabeledValueRow
        label="Parent"
        value={
          retailer?.parent && (
            <MaybeRetailerLink
              retailer={retailer.parent}
              onClick={onSelectedRetailerIdChange}
            />
          )
        }
      />

      <LabeledValueRow
        label="Description"
        value={
          retailer?.description && (
            <MaxHeightExpander>
              <MarkdownText markdown={retailer.description} />
            </MaxHeightExpander>
          )
        }
        rowProps={{ gridColumn: "1/-1" }}
      />

      <LabeledValueRow
        label="Channels"
        value={
          !!retailer?.channelsList?.length && (
            <TagList
              items={retailer?.channelsList}
              getKey={(c) => c.id}
              getLabel={(c) => c.name}
              variant="outline"
              colorScheme="pinkButton"
              color="magenta.veryDark"
            />
          )
        }
        isValueLoaded={!!retailer?.channelsList}
        isValuePresent={!!retailer?.channelsList?.length}
      />

      <LabeledValueRow
        label="Regions"
        value={
          !!retailer?.regionsList?.length && (
            <TagList
              items={retailer?.regionsList}
              getKey={(r) => r.id}
              getLabel={(r) => r.name}
              variant="outline"
              colorScheme="pinkButton"
              color="magenta.veryDark"
            />
          )
        }
        isValueLoaded={!!retailer?.regionsList}
        isValuePresent={!!retailer?.regionsList?.length}
      />

      {retailer?.retailerType === RetailerType.INDEPENDENT && isAdmin && (
        <LabeledValueRow label="Affiliation" value={retailer?.affiliation} />
      )}

      <LabeledValueRow
        label="Slotting Required?"
        value={
          retailer?.slottingRequired &&
          slottingOptionsMap[retailer.slottingRequired]
        }
        rowProps={{ gridColumn: 1 }}
      />

      <LabeledValueRow
        label="Pricing Strategy"
        value={
          retailer?.pricingStrategy &&
          pricingStrategyMap[retailer.pricingStrategy]
        }
      />

      <LabeledValueRow
        label="Category Review?"
        value={
          retailer?.categoryReview &&
          retailerCategoryReviewMap[retailer.categoryReview]
        }
      />

      <LabeledValueRow
        label="Headquarters Location"
        value={
          retailer?.headquartersAddress && (
            <Address address={retailer.headquartersAddress} />
          )
        }
        rowProps={{ gridColumn: 1 }}
      />

      <LabeledValueRow
        label="Headquarters Phone"
        value={
          retailer?.headquartersPhoneNumber && (
            <PhoneNumberLink phoneNumber={retailer.headquartersPhoneNumber} />
          )
        }
      />

      <LabeledValueRow
        label="Distributors"
        value={
          !!retailer?.distributionCentersList?.length && (
            <TagList
              items={retailer.distributionCentersList}
              getKey={(dc) => dc.id}
              getLabel={(dc) => (
                <Link
                  href={(() => {
                    if (dc.name === null) {
                      if (isAdmin) {
                        return adminOrderingPlatformPage(dc.orderingPlatformId);
                      }
                      return customerPage(dc.orderingPlatformId);
                    }
                    if (isAdmin) return adminDistributionCenterPage(dc.id);
                    return customerWarehousePage(dc.orderingPlatformId, dc.id);
                  })()}
                >
                  {dc.fullDisplayName}
                </Link>
              )}
              variant="outline"
              colorScheme="pinkButton"
              color="magenta.veryDark"
            />
          )
        }
        isValueLoaded={!!retailer?.distributionCentersList}
        isValuePresent={!!retailer?.distributionCentersList?.length}
        rowProps={{ gridColumn: "1/-1" }}
      />

      {brokers !== undefined && (
        <LabeledValueRow
          label="Brokers"
          value={brokers?.length && <BrokerTagList brokers={brokers} />}
          isValuePresent={!!brokers?.length}
        />
      )}

      <LabeledValueRow
        label="Tags"
        value={
          !!retailer?.tags?.length && (
            <TagList
              items={retailer.tags}
              getKey={(t) => String(t)}
              getLabel={(t) => String(t)}
              variant="outline"
              colorScheme="pinkButton"
              color="magenta.veryDark"
            />
          )
        }
        isValueLoaded={!!retailer?.tags}
        isValuePresent={!!retailer?.tags?.length}
      />

      {(isAdmin || retailer?.isCustom) && (
        <LabeledValueRow
          label="Notes"
          value={
            retailer?.notes && (
              <MaxHeightExpander>
                <Box whiteSpace="pre-wrap">{retailer.notes}</Box>
              </MaxHeightExpander>
            )
          }
          rowProps={{ gridColumn: "1/-1" }}
        />
      )}

      <LabeledValueRow
        label="Banners"
        value={
          retailer?.childRetailersList?.length && (
            <MaxHeightExpander>
              <UnorderedList>
                {retailer.childRetailersList.map((cr) => (
                  <ListItem key={cr.id}>
                    <MaybeRetailerLink
                      retailer={cr}
                      onClick={onSelectedRetailerIdChange}
                    />
                  </ListItem>
                ))}
              </UnorderedList>
            </MaxHeightExpander>
          )
        }
        isValueLoaded={!!retailer?.childRetailersList}
        isValuePresent={!!retailer?.childRetailersList?.length}
      />

      {children}
    </LabeledValueTable>
  );
}
